import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from '../../providers/auth/auth.service';
import { CustomerProvider } from '../../providers/customer/customer';
import { HelpersProvider } from '../../providers/helpers/helpers';
import { ProgramProvider } from '../../providers/program/program';
import { UserProvider } from '../../providers/user/user';
import packageJson from '../../../package.json';

type DashboardV2Tone = 'success' | 'warning' | 'muted' | 'danger';

interface DashboardV2Kpi {
  icon: string;
  iconBg: string;
  label: string;
  value: string;
  limit?: string;
  helper: string;
  tone: DashboardV2Tone;
}

interface DashboardV2ConsultancyStudent {
  id?: number;
  email?: string;
  phone?: string;
  ddi?: string;
  avatar?: string;
  initials: string;
  name: string;
  percent: number;
  meta: string;
  color: string;
}

interface DashboardV2LegendItem {
  label: string;
  value: number;
  color: string;
}

interface DashboardV2BarItem {
  label: string;
  value: number;
  percent: number;
  color: string;
}

interface DashboardV2RankingItem {
  customerId?: number;
  customerEmail?: string;
  position: string;
  initials: string;
  name: string;
  points: string;
  trainings: number;
  programs: number;
  level: number;
  feedbacks: number;
}

interface DashboardV2RecentTraining {
  id?: number;
  email?: string;
  initials: string;
  name: string;
  description: string;
}

interface DashboardV2ChartBar {
  label: string;
  fullLabel: string;
  value: number;
  height: number;
  y: number;
  active: boolean;
}

interface DashboardV2Feedback {
  customerId?: number;
  customerEmail?: string;
  initials: string;
  student: string;
  program: string;
  training: string;
  date: string;
  feedback: string;
}

@Component({
  selector: 'app-dashboard-v2',
  templateUrl: './dashboard-v2.component.html',
  styleUrls: ['./dashboard-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardV2Component implements OnDestroy, OnInit {
  private readonly dashboardBodyClass = 'dashboard-v2-active';
  public version: string = packageJson.version;
  greeting = 'Olá, Vinicius 👋';
  subtitle = 'Aqui está o resumo da sua consultoria hoje';
  planLabel = '★ PLANO BLACK · até 29/12/2027';
  studentActivePercent = 71;
  programSummary = 'Programas · 49/700';
  consultancyPagerLabel = 'Mostrando 1–7 de 17';
  consultancyPageLabel = '‹ Página 1 de 3 ›';
  feedbackPagerLabel = 'Mostrando 1–4 de 23 feedbacks';
  activeRankingTab = 'Todo o período';
  activeTrainingRange = 'Ver 30 dias';
  trainingFilterDays = 30;
  trainingRangeTabs = ['Ver 7 dias', 'Ver 15 dias', 'Ver 30 dias'];
  chartBarGap = 58;
  feedbackPages = ['‹', '1', '2', '3', '›'];
  activeFeedbackPage = '1';
  consultancyPageIndex = 0;
  consultancyPageSize = 7;
  feedbackPageIndex = 0;
  feedbackPageSize = 4;
  user: any = {};
  loading = {
    consultancy: false,
    ranking: false,
    trainings: false,
    feedbacks: false,
  };
  errors = {
    consultancy: '',
    ranking: '',
    trainings: '',
    feedbacks: '',
  };
  private currentAccountLimits: any = {};
  private trainingsDoneResult: any = null;
  private allConsultancyStudents: any[] = [];
  private allFeedbacks: any[] = [];
  private hasKpiCache = false;

  kpis: DashboardV2Kpi[] = [
    { icon: 'assets/imgs/dashboard-kpis/alunos-cadastrados.svg', iconBg: 'var(--layout-kpi-green-bg)', label: 'Alunos cadastrados', value: '—', limit: '', helper: 'Carregando', tone: 'muted' },
    { icon: 'assets/imgs/dashboard-kpis/lista-espera.svg', iconBg: 'var(--layout-kpi-amber-bg)', label: 'Na lista de espera', value: '—', limit: '', helper: 'Carregando', tone: 'muted' },
    { icon: 'assets/imgs/dashboard-kpis/programas-cadastrados.svg', iconBg: 'var(--layout-kpi-blue-bg)', label: 'Programas cadastrados', value: '—', limit: '', helper: 'Carregando', tone: 'muted' },
    { icon: 'assets/imgs/dashboard-kpis/alunos-bloqueados.svg', iconBg: 'var(--layout-kpi-red-bg)', label: 'Alunos bloqueados', value: '—', limit: '', helper: 'Carregando', tone: 'muted' },
  ];

  consultancyStudents: DashboardV2ConsultancyStudent[] = [];

  studentStatus: DashboardV2LegendItem[] = [
    { label: 'Ativos', value: 12, color: 'var(--layout-green)' },
    { label: 'Expirados', value: 0, color: 'var(--layout-red)' },
    { label: 'Bloqueados', value: 5, color: 'var(--layout-text-5)' },
  ];

  programObjectives: DashboardV2BarItem[] = [
    { label: 'Definição/Emagrecimento', value: 44, percent: 90, color: 'var(--layout-red)' },
    { label: 'Ganhar Massa Muscular', value: 42, percent: 86, color: 'var(--layout-accent-2)' },
    { label: 'Cardiovascular', value: 4, percent: 8, color: 'var(--layout-cyan)' },
    { label: 'Fortalec. Muscular', value: 2, percent: 4, color: 'var(--layout-yellow)' },
  ];

  programDifficulties: DashboardV2BarItem[] = [
    { label: 'Iniciante', value: 21, percent: 43, color: 'var(--layout-green)' },
    { label: 'Intermediário', value: 15, percent: 31, color: 'var(--layout-yellow)' },
    { label: 'Avançado', value: 12, percent: 24, color: 'var(--layout-red)' },
  ];

  rankingTabs = ['Semanal', 'Mensal', 'Anual', 'Todo o período'];

  ranking: DashboardV2RankingItem[] = [];

  recentTrainings: DashboardV2RecentTraining[] = [];

  chartBars: DashboardV2ChartBar[] = [];

  feedbacks: DashboardV2Feedback[] = [];

  constructor(
    private auth: AuthService,
    private customerProvider: CustomerProvider,
    private helpers: HelpersProvider,
    private programProvider: ProgramProvider,
    private router: Router,
    private toastr: ToastrService,
    private userProvider: UserProvider,
  ) {}

  ngOnInit(): void {
    document.body.classList.add(this.dashboardBodyClass);
    this.user = this.auth.retrieveLocalUser() || {};
    this.applyUserHeader();
    this.loadKpiCache();

    if (!this.hasKpiCache) {
      this.applySubscriptionData(this.user.subs || {});
    }

    this.loadSubscriptionData();
    this.loadWaitline();
    this.loadPrograms();
    this.loadConsultancyStudents();
    this.loadRanking();
    this.loadTrainingsDone();
    this.loadFeedbacks();
  }

  ngOnDestroy(): void {
    document.body.classList.remove(this.dashboardBodyClass);
  }

  private applyUserHeader(): void {
    const name = this.getFirstName(this.user.name);
    this.greeting = 'Olá, ' + name + ' 👋';
  }

  private loadKpiCache(): void {
    const cache = this.helpers.getLocallyStored(this.kpiCacheName(), false, null);

    if (!cache || !Array.isArray(cache.kpis)) {
      this.hasKpiCache = false;
      return;
    }

    this.kpis = cache.kpis.map((kpi: DashboardV2Kpi) => Object.assign({}, kpi, {
      iconBg: this.kpiIconBackground(kpi.label),
    }));
    this.studentActivePercent = this.toNumber(cache.studentActivePercent);

    if (Array.isArray(cache.studentStatus)) {
      this.studentStatus = cache.studentStatus.map((status: DashboardV2LegendItem) => Object.assign({}, status, {
        color: this.studentStatusColor(status.label),
      }));
    }

    if (cache.programSummary) {
      this.programSummary = cache.programSummary;
    }

    this.hasKpiCache = true;
  }

  private saveKpiCache(): void {
    this.helpers.storeLocally(this.kpiCacheName(), {
      kpis: this.kpis,
      studentActivePercent: this.studentActivePercent,
      studentStatus: this.studentStatus,
      programSummary: this.programSummary,
    }, 0);
  }

  private kpiCacheName(): string {
    const userId = (this.user && this.user.id) ? this.user.id : 'user';
    return 'dashboardV2KpiCache_' + userId;
  }

  private kpiIconBackground(label: string): string {
    if (label === 'Alunos cadastrados') return 'var(--layout-kpi-green-bg)';
    if (label === 'Na lista de espera') return 'var(--layout-kpi-amber-bg)';
    if (label === 'Programas cadastrados') return 'var(--layout-kpi-blue-bg)';
    return 'var(--layout-kpi-red-bg)';
  }

  private studentStatusColor(label: string): string {
    if (label === 'Ativos') return 'var(--layout-green)';
    if (label === 'Expirados') return 'var(--layout-red)';
    return 'var(--layout-text-5)';
  }

  private loadSubscriptionData(): void {
    this.userProvider.subscriptions().then(
      (res: any) => {
        const purchase = (res && res.purchase) ? res.purchase : {};
        const teacher = (res && res.teacher) ? res.teacher : {};
        const accountLimits = this.resolveAccountLimits(res);

        this.user = this.auth.retrieveLocalUser() || this.user;
        this.user.subs = Object.assign({}, purchase, accountLimits);
        this.user.premium_expires_date = teacher.premium_expires_date || this.user.premium_expires_date;

        this.applySubscriptionData(accountLimits);
        this.saveKpiCache();
        this.applyPlanLabel(purchase, teacher);
      },
      (err: any) => {
        this.toastr.error((err && err.message) || err || 'Não foi possível carregar os dados do plano.', '', this.helpers.toastrCfgs);
      }
    );
  }

  private loadWaitline(): void {
    this.customerProvider.getCustomersWaiting().then(
      (res: any) => {
        const customers = (res && res.customers) ? res.customers : [];
        this.updateWaitlineKpi(customers.length || 0);
        this.saveKpiCache();
      },
      (err: any) => {
        this.toastr.error((err && err.message) || err || 'Não foi possível carregar a lista de espera.', '', this.helpers.toastrCfgs);
      }
    );
  }

  private loadPrograms(): void {
    this.programProvider.programs().then(
      (res: any) => {
        const list = Array.isArray(res) ? res : ((res && Array.isArray(res.programs)) ? res.programs : []);
        this.applyProgramsData(list);
        this.saveKpiCache();
      },
      (err: any) => {
        this.toastr.error((err && err.message) || err || 'Não foi possível carregar os programas.', '', this.helpers.toastrCfgs);
      }
    );
  }

  loadConsultancyStudents(force: boolean = false): void {
    this.loading.consultancy = true;
    this.errors.consultancy = '';
    const request = (this.user && (this.user.account_type === 'master' || this.user.account_type === 'admin'))
      ? this.customerProvider.getCustomersPremium()
      : this.customerProvider.getCustomers();

    request.then(
      (res: any) => {
        const students = (res && Array.isArray(res.customers)) ? res.customers : [];
        this.allConsultancyStudents = students;
        this.consultancyPageIndex = 0;
        this.applyConsultancyStudents();
        this.loading.consultancy = false;
      },
      (err: any) => {
        this.errors.consultancy = (err && err.message) || err || 'Não foi possível carregar os alunos.';
        this.loading.consultancy = false;
        this.toastr.error(this.errors.consultancy, '', this.helpers.toastrCfgs);
      }
    );
  }

  loadRanking(period: string = 'all'): void {
    this.loading.ranking = true;
    this.errors.ranking = '';
    this.customerProvider.admGetRanking({
      limit: 7,
      offset: 0,
      metric: 'points',
      period: period,
    }).then(
      (res: any) => {
        this.applyRankingData(res);
        this.loading.ranking = false;
      },
      (err: any) => {
        this.errors.ranking = (err && err.message) || err || 'Não foi possível carregar o ranking.';
        this.loading.ranking = false;
        this.toastr.error(this.errors.ranking, '', this.helpers.toastrCfgs);
      }
    );
  }

  loadTrainingsDone(): void {
    this.loading.trainings = true;
    this.errors.trainings = '';
    this.customerProvider.getLastDone().then(
      (res: any) => {
        this.trainingsDoneResult = res;
        this.applyTrainingsDoneData(res);
        this.loading.trainings = false;
      },
      (err: any) => {
        this.errors.trainings = (err && err.message) || err || 'Não foi possível carregar os treinos concluídos.';
        this.loading.trainings = false;
        this.toastr.error(this.errors.trainings, '', this.helpers.toastrCfgs);
      }
    );
  }

  loadFeedbacks(): void {
    this.loading.feedbacks = true;
    this.errors.feedbacks = '';
    this.customerProvider.getAllFeedbacks().then(
      (res: any) => {
        const feedbacks = (res && Array.isArray(res.feedbacks)) ? res.feedbacks : [];
        this.allFeedbacks = feedbacks;
        this.feedbackPageIndex = 0;
        this.applyFeedbacksData();
        this.loading.feedbacks = false;
      },
      (err: any) => {
        this.errors.feedbacks = (err && err.message) || err || 'Não foi possível carregar os feedbacks.';
        this.loading.feedbacks = false;
        this.toastr.error(this.errors.feedbacks, '', this.helpers.toastrCfgs);
      }
    );
  }

  refreshRanking(): void {
    this.loadRanking(this.rankingPeriodValue(this.activeRankingTab));
  }

  changeRankingTab(tab: string): void {
    this.activeRankingTab = tab;
    this.loadRanking(this.rankingPeriodValue(tab));
  }

  changeTrainingRange(range: string): void {
    this.activeTrainingRange = range;
    this.trainingFilterDays = Number(range.replace(/\D/g, '')) || 30;
    if (this.trainingsDoneResult) {
      this.applyTrainingsDoneData(this.trainingsDoneResult);
    }
  }

  goToPrograms(): void {
    this.router.navigate(['professor/programas'], { skipLocationChange: true });
  }

  handleKpiClick(kpi: DashboardV2Kpi): void {
    if (!kpi) {
      return;
    }

    if (kpi.label === 'Na lista de espera') {
      this.goToWaitline();
      return;
    }

    if (kpi.label === 'Alunos cadastrados' || kpi.label === 'Alunos bloqueados') {
      this.router.navigate(['professor/alunos/0'], { skipLocationChange: true });
      return;
    }

    if (kpi.label === 'Programas cadastrados') {
      this.goToPrograms();
    }
  }

  goToWaitline(): void {
    this.router.navigate(['professor/alunos/-1'], { skipLocationChange: true });
  }

  goToStudent(student: any): void {
    if (!student) {
      return;
    }

    if (this.user && this.user.account_type === 'master') {
      this.router.navigate(['clientes/' + (student.email || student.customerEmail || student.id || student.customerId)], { skipLocationChange: true });
      return;
    }

    const id = student.id || student.customerId;

    if (!id) {
      return;
    }

    this.router.navigate(['professor/alunos/' + id], { skipLocationChange: true });
  }

  goToWhats(student: DashboardV2ConsultancyStudent): void {
    if (!student || !student.phone) {
      this.toastr.error('Telefone não cadastrado.', '', this.helpers.toastrCfgs);
      return;
    }

    const ddi = (student.ddi || '55').replace(/^0+(?!\.|$)/, '');
    window.open('https://wa.me/' + ddi + student.phone, '_system', 'location=yes');
  }

  goToTutorial(): void {
    window.open('https://www.stcoach.app/tutoriais-aluno', '_system', 'location=yes');
  }

  changeConsultancyPage(direction: number): void {
    const totalPages = Math.max(Math.ceil(this.allConsultancyStudents.length / this.consultancyPageSize), 1);
    this.consultancyPageIndex = Math.max(Math.min(this.consultancyPageIndex + direction, totalPages - 1), 0);
    this.applyConsultancyStudents();
  }

  changeFeedbackPage(page: string): void {
    const totalPages = Math.max(Math.ceil(this.allFeedbacks.length / this.feedbackPageSize), 1);

    if (page === '‹') {
      this.feedbackPageIndex = Math.max(this.feedbackPageIndex - 1, 0);
    } else if (page === '›') {
      this.feedbackPageIndex = Math.min(this.feedbackPageIndex + 1, totalPages - 1);
    } else {
      this.feedbackPageIndex = Math.max(Math.min(Number(page) - 1, totalPages - 1), 0);
    }

    this.applyFeedbacksData();
  }

  @HostListener('window:resize')
  updateFeedbackPaginationForViewport(): void {
    if (this.allFeedbacks.length === 0) {
      return;
    }

    const totalPages = Math.max(Math.ceil(this.allFeedbacks.length / this.feedbackPageSize), 1);
    this.feedbackPages = this.buildFeedbackPages(totalPages);
  }

  private applySubscriptionData(subs: any): void {
    const accountLimits = this.resolveAccountLimits(subs);
    const students = accountLimits.students;
    const programs = accountLimits.programs;

    const studentsTotal = this.toNumber(students.total);
    const studentsMax = this.toNumber(students.max);
    const studentsExpired = this.toNumber(students.expireds);
    const studentsBlocked = this.toNumber(students.inactives);
    const studentsActive = Math.max(studentsTotal - studentsExpired - studentsBlocked, 0);
    const studentsActivePercent = studentsTotal > 0 ? Math.round((studentsActive / studentsTotal) * 100) : 0;
    const studentsLimitPercent = studentsMax > 0 ? Math.round((studentsTotal / studentsMax) * 100) : 0;

    const programsTotal = this.toNumber(programs.total);
    const programsMax = this.toNumber(programs.max);
    const programsPercent = programsMax > 0 ? Math.round((programsTotal / programsMax) * 100) : 0;

    this.studentActivePercent = studentsActivePercent;
    this.studentStatus = [
      { label: 'Ativos', value: studentsActive, color: 'var(--layout-green)' },
      { label: 'Expirados', value: studentsExpired, color: 'var(--layout-red)' },
      { label: 'Bloqueados', value: studentsBlocked, color: 'var(--layout-text-5)' },
    ];

    this.kpis = this.kpis.map((kpi: DashboardV2Kpi) => {
      if (kpi.label === 'Alunos cadastrados') {
        return {
          icon: kpi.icon,
          iconBg: kpi.iconBg,
          label: kpi.label,
          value: String(studentsTotal),
          limit: studentsMax > 0 ? '/ ' + studentsMax : '',
          helper: studentsMax > 0 ? studentsActive + ' ativos · ' + studentsLimitPercent + '% do limite' : studentsActive + ' ativos',
          tone: 'success',
        };
      }

      if (kpi.label === 'Programas cadastrados') {
        return {
          icon: kpi.icon,
          iconBg: kpi.iconBg,
          label: kpi.label,
          value: String(programsTotal),
          limit: programsMax > 0 ? '/ ' + programsMax : '',
          helper: programsMax > 0 ? programsPercent + '% do limite' : 'Total cadastrado',
          tone: programsMax > 0 ? 'muted' : 'success',
        };
      }

      if (kpi.label === 'Alunos bloqueados') {
        return {
          icon: kpi.icon,
          iconBg: kpi.iconBg,
          label: kpi.label,
          value: String(studentsBlocked),
          limit: '',
          helper: studentsBlocked > 0 ? 'Requer atenção' : 'Sem bloqueios',
          tone: studentsBlocked > 0 ? 'danger' : 'success',
        };
      }

      return kpi;
    });

    if (programsMax > 0) {
      this.programSummary = 'Programas · ' + programsTotal + '/' + programsMax;
    } else if (programsTotal > 0) {
      this.programSummary = 'Programas · ' + programsTotal;
    }
  }

  private applyConsultancyStudents(): void {
    const students = this.allConsultancyStudents;
    const start = this.consultancyPageIndex * this.consultancyPageSize;
    const end = start + this.consultancyPageSize;
    const visibleStudents = students.slice(start, end);

    this.consultancyStudents = visibleStudents.map((student: any) => {
      const percent = this.premiumPercent(student && student.premium_expires_date);

      return {
        id: student && student.id,
        email: student && student.email,
        phone: student && student.details && student.details.phone,
        ddi: student && student.details && student.details.ddi,
        avatar: student && student.details && student.details.avatar,
        initials: this.initials(student && student.name),
        name: this.firstName((student && student.name) || (student && student.email) || 'Aluno'),
        percent: percent,
        meta: this.consultancyMeta(student, percent),
        color: this.consultancyColor(percent),
      };
    });

    const totalPages = Math.max(Math.ceil(students.length / this.consultancyPageSize), 1);
    this.consultancyPagerLabel = 'Mostrando ' + (visibleStudents.length > 0 ? start + 1 : '0') + '–' + Math.min(end, students.length) + ' de ' + students.length;
    this.consultancyPageLabel = '‹ Página ' + (this.consultancyPageIndex + 1) + ' de ' + totalPages + ' ›';
  }

  private applyRankingData(res: any): void {
    const items = (res && Array.isArray(res.items)) ? res.items : [];

    this.ranking = items.map((item: any, index: number) => {
      const position = item.rank || (index + 1);

      return {
        customerId: item.customer_id,
        customerEmail: item.customer_email,
        position: this.rankingPosition(position),
        initials: this.initials(item.customer_name),
        name: item.customer_name || 'Aluno oculto',
        points: this.numberValue(item.points_total),
        trainings: this.toNumber(item.trainings_done),
        programs: this.toNumber(item.programs_done),
        level: this.toNumber(item.level),
        feedbacks: this.toNumber(item.feedbacks_count),
      };
    });
  }

  private applyTrainingsDoneData(res: any): void {
    const lastTrainings = (res && Array.isArray(res.lastTrainings)) ? res.lastTrainings : [];
    const trainingDoneByDay = (res && Array.isArray(res.trainingDoneByDay)) ? res.trainingDoneByDay : [];

    this.recentTrainings = lastTrainings.slice(0, 6).map((training: any) => ({
      id: training.id,
      email: training.email,
      initials: this.initials(training.name),
      name: this.firstName(training.name || 'Aluno'),
      description: training.lastTraining || '',
    }));

    const bars = trainingDoneByDay
      .slice(0, this.trainingFilterDays)
      .reverse()
      .slice(-7);
    const max = bars.reduce((acc: number, row: any) => Math.max(acc, this.toNumber(row.value)), 0);
    this.chartBarGap = bars.length > 1 ? Math.floor(348 / (bars.length - 1)) : 58;

    this.chartBars = bars.map((row: any, index: number) => {
      const value = this.toNumber(row.value);
      const height = max > 0 ? Math.max(Math.round((value / max) * 160), 12) : 0;

      return {
        label: this.formatShortDate(row.name),
        fullLabel: this.formatDate(row.name),
        value: value,
        height: height,
        y: 200 - height,
        active: index === bars.length - 1,
      };
    });
  }

  private applyFeedbacksData(): void {
    const feedbacks = this.allFeedbacks;
    const start = this.feedbackPageIndex * this.feedbackPageSize;
    const end = start + this.feedbackPageSize;
    const pageItems = feedbacks.slice(start, end);
    const totalPages = Math.max(Math.ceil(feedbacks.length / this.feedbackPageSize), 1);

    this.feedbacks = pageItems.map((item: any) => ({
      customerId: item.customer_id,
      customerEmail: item.customer_email,
      initials: this.initials(item.customer_name),
      student: item.customer_name || 'Aluno',
      program: item.program_name || '-',
      training: item.training_name || '-',
      date: this.formatDate(item.created_at),
      feedback: item.feedback || '',
    }));

    this.feedbackPagerLabel = 'Mostrando ' + (this.feedbacks.length > 0 ? start + 1 : '0') + '–' + Math.min(end, feedbacks.length) + ' de ' + feedbacks.length + ' feedbacks';
    this.feedbackPages = this.buildFeedbackPages(totalPages);
    this.activeFeedbackPage = String(this.feedbackPageIndex + 1);
  }

  private resolveAccountLimits(source: any): any {
    const candidates = [
      source,
      source && source.purchase,
      this.user && this.user.subs,
      this.currentAccountLimits,
    ];

    const studentsSource = candidates.find((candidate: any) => candidate && candidate.students);
    const programsSource = candidates.find((candidate: any) => candidate && candidate.programs);

    const accountLimits = {
      students: studentsSource && studentsSource.students ? studentsSource.students : {},
      programs: programsSource && programsSource.programs ? programsSource.programs : {},
    };

    if (accountLimits.students.total !== undefined || accountLimits.programs.total !== undefined) {
      this.currentAccountLimits = accountLimits;
    }

    return accountLimits;
  }

  private applyPlanLabel(purchase: any, teacher: any): void {
    const title = (purchase && purchase.supertreinos_plan_title) ? purchase.supertreinos_plan_title : '';
    const planName = this.extractPlanName(title);
    const expiresAt = (teacher && teacher.premium_expires_date) || this.user.premium_expires_date;
    const formattedDate = this.formatDate(expiresAt);

    this.planLabel = '★ PLANO ' + planName + (formattedDate ? ' · até ' + formattedDate : '');
  }

  private updateWaitlineKpi(total: number): void {
    this.kpis = this.kpis.map((kpi: DashboardV2Kpi) => {
      if (kpi.label !== 'Na lista de espera') {
        return kpi;
      }

      return {
        icon: kpi.icon,
        iconBg: kpi.iconBg,
        label: kpi.label,
        value: String(total),
        limit: '',
        helper: total > 0 ? 'Ver lista →' : 'Fila vazia',
        tone: total > 0 ? 'warning' : 'success',
      };
    });
  }

  private applyProgramsData(programs: any[]): void {
    const total = programs.length;

    this.programObjectives = this.buildGoalBars(programs);
    this.programDifficulties = this.buildDifficultyBars(programs);

    if (this.user && this.user.subs && this.user.subs.programs && this.user.subs.programs.max) {
      this.programSummary = 'Programas · ' + total + '/' + this.user.subs.programs.max;
    } else {
      this.programSummary = 'Programas · ' + total;
    }

    this.kpis = this.kpis.map((kpi: DashboardV2Kpi) => {
      if (kpi.label !== 'Programas cadastrados') {
        return kpi;
      }

      const max = this.user && this.user.subs && this.user.subs.programs ? this.toNumber(this.user.subs.programs.max) : 0;
      const percent = max > 0 ? Math.round((total / max) * 100) : 0;

      return {
        icon: kpi.icon,
        iconBg: kpi.iconBg,
        label: kpi.label,
        value: String(total),
        limit: max > 0 ? '/ ' + max : '',
        helper: max > 0 ? percent + '% do limite' : 'Total cadastrado',
        tone: 'muted',
      };
    });
  }

  private buildGoalBars(programs: any[]): DashboardV2BarItem[] {
    const colors = ['var(--layout-red)', 'var(--layout-accent-2)', 'var(--layout-cyan)', 'var(--layout-yellow)', 'var(--layout-green)', 'var(--layout-purple)'];
    const totals: any = {};

    programs.forEach((program: any) => {
      if (program && program.goals && program.goals[0]) {
        const goal = program.goals[0];
        const label = this.helpers.returnTranslation(goal);
        totals[label] = (totals[label] || 0) + 1;
      }
    });

    return this.toSortedBars(totals, colors);
  }

  private buildDifficultyBars(programs: any[]): DashboardV2BarItem[] {
    const totals: any = {};
    const difficulties = [
      { label: 'Iniciante', color: 'var(--layout-green)' },
      { label: 'Intermediário', color: 'var(--layout-yellow)' },
      { label: 'Avançado', color: 'var(--layout-red)' },
      { label: 'Expert', color: 'var(--layout-magenta)' },
    ];

    programs.forEach((program: any) => {
      const label = (program && program.difficulty_level) ? program.difficulty_level : 'Não informado';
      totals[label] = (totals[label] || 0) + 1;
    });

    const max = difficulties.reduce((largest: number, difficulty: any) => {
      return Math.max(largest, totals[difficulty.label] || 0);
    }, 0);

    return difficulties.map((difficulty: any) => {
      const value = totals[difficulty.label] || 0;

      return {
        label: difficulty.label,
        value: value,
        percent: max > 0 ? Math.round((value / max) * 100) : 0,
        color: difficulty.color,
      };
    });
  }

  private toSortedBars(totals: any, colors: string[]): DashboardV2BarItem[] {
    const rows = Object.keys(totals)
      .map((label: string) => ({ label: label, value: totals[label] }))
      .sort((a: any, b: any) => b.value - a.value);

    const max = rows.length > 0 ? rows[0].value : 0;

    return rows.slice(0, 4).map((row: any, index: number) => ({
      label: row.label,
      value: row.value,
      percent: max > 0 ? Math.round((row.value / max) * 100) : 0,
      color: colors[index] || 'var(--layout-accent-2)',
    }));
  }

  private buildFeedbackPages(totalPages: number): string[] {
    const pages = ['‹'];
    const pageLimit = window.innerWidth <= 768 ? 4 : 5;
    const visiblePages = Math.min(totalPages, pageLimit);
    const currentPage = this.feedbackPageIndex + 1;
    let firstPage = Math.max(currentPage - 2, 1);

    if ((firstPage + visiblePages - 1) > totalPages) {
      firstPage = Math.max(totalPages - visiblePages + 1, 1);
    }

    for (let index = 0; index < visiblePages; index++) {
      pages.push(String(firstPage + index));
    }

    pages.push('›');

    return pages;
  }

  private rankingPeriodValue(tab: string): string {
    if (tab === 'Semanal') return 'week';
    if (tab === 'Mensal') return 'month';
    if (tab === 'Anual') return 'year';
    return 'all';
  }

  private rankingPosition(position: any): string {
    const normalized = this.toNumber(position);
    if (normalized === 1) return '🥇';
    if (normalized === 2) return '🥈';
    if (normalized === 3) return '🥉';
    return String(position);
  }

  private premiumPercent(date: any): number {
    if (!date) {
      return 0;
    }

    const percent = this.helpers.checkPremiumExpire(date, '%');

    if (percent === 'expirado') {
      return 0;
    }

    return Math.max(Math.min(this.toNumber(percent), 100), 0);
  }

  private consultancyMeta(student: any, percent: number): string {
    if (student && student.status === 9) {
      return 'Bloqueado';
    }

    const expiresAt = student && student.premium_expires_date ? this.formatDate(student.premium_expires_date) : '';
    const days = this.daysUntil(student && student.premium_expires_date);

    if (days !== null && days <= 30) {
      return 'Vence em ' + Math.max(days, 0) + ' dias' + (expiresAt ? ' · ' + expiresAt : '');
    }

    return percent + '% do plano';
  }

  private consultancyColor(percent: number): string {
    if (percent < 35) return 'var(--layout-red)';
    if (percent < 65) return 'var(--layout-accent-2)';
    return 'var(--layout-green)';
  }

  private daysUntil(date: any): number | null {
    if (!date) {
      return null;
    }

    const target = new Date(date);

    if (isNaN(target.getTime())) {
      return null;
    }

    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const targetOnly = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();

    return Math.ceil((targetOnly - todayOnly) / (1000 * 60 * 60 * 24));
  }

  private extractPlanName(title: string): string {
    const parts = (title || '').trim().split(/\s+/);
    return (parts[3] || parts[2] || title || 'ATUAL').toUpperCase();
  }

  private formatDate(date: string): string {
    if (!date) {
      return '';
    }

    const onlyDate = String(date).split('T')[0].split(' ')[0];
    const parts = onlyDate.split('-');

    if (parts.length === 3) {
      return parts[2] + '/' + parts[1] + '/' + parts[0];
    }

    return String(date);
  }

  private formatShortDate(date: string): string {
    const formatted = this.formatDate(date);

    if (!formatted || formatted.indexOf('/') === -1) {
      return formatted;
    }

    const parts = formatted.split('/');
    return parts[0] + '/' + parts[1];
  }

  private getFirstName(name: string): string {
    if (!name) {
      return 'Professor';
    }

    return name.trim().split(/\s+/)[0] || 'Professor';
  }

  private firstName(name: string): string {
    return this.getFirstName(name).split('@')[0];
  }

  private initials(name: string): string {
    if (!name) {
      return 'A';
    }

    const parts = name.trim().split(/\s+/).filter((part: string) => !!part);

    if (parts.length === 0) {
      return 'A';
    }

    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  private numberValue(value: any): string {
    const numericValue = Number(value);

    if (value === null || value === undefined) {
      return '0';
    }

    if (isNaN(numericValue)) {
      return String(value);
    }

    return numericValue.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return isNaN(parsed) ? 0 : parsed;
  }
}
